import { BRAND } from '../brand';
import { escapeHtml, formatReportJobDate, formatReportTimestamp } from './jobReport';

/**
 * דוח PDF ממותג של יומן הפעילות של עבודה אחת.
 * הממשק מכין את השורות (כבר מתורגמות לעברית), והתבנית מסדרת אותן לדף A4 להדפסה או לשמירה כ-PDF.
 */

export interface ActivityReportRow {
  at: Date;
  actor: string;
  /** האם הפעולה בוצעה ע"י המערכת או ע"י קבלן (להבחנה ויזואלית) */
  actorKind: 'admin' | 'system' | 'contractor';
  action: string;
  /** שורות פירוט, למשל "שעת התייצבות: 18:30 ← 17:45" */
  details: string[];
}

export interface ActivityReportInput {
  job: { name: string; date: string; reportTime: string; location: string };
  rows: ActivityReportRow[];
  generatedAt: Date;
  logoSrc: string;
}

export function renderActivityReportHtml(input: ActivityReportInput): string {
  const c = BRAND.colors;
  const e = escapeHtml;
  const rows = [...input.rows].sort((a, b) => b.at.getTime() - a.at.getTime());
  const generated = formatReportTimestamp(input.generatedAt);
  const kindLabel = { admin: 'מנהל', system: 'מערכת', contractor: 'נרשם' } as const;

  const body = rows.length
    ? rows
        .map(
          (r) => `
      <tr>
        <td class="when ltr">${formatReportTimestamp(r.at)}</td>
        <td><div class="actor">${e(r.actor)}</div><div class="kind kind-${r.actorKind}">${kindLabel[r.actorKind]}</div></td>
        <td><div class="action">${e(r.action)}</div>${r.details.map((d) => `<div class="detail">${e(d)}</div>`).join('')}</td>
      </tr>`,
        )
        .join('')
    : `<tr><td colspan="3" class="empty">אין פעולות ביומן</td></tr>`;

  return `<!doctype html>
<html lang="he" dir="rtl">
<head>
<meta charset="utf-8" />
<title>${e(BRAND.name)} — יומן פעילות — ${e(input.job.name)}</title>
<style>
@page { size: A4; margin: 14mm 14mm 16mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body { font-family: 'Heebo', 'Arial Hebrew', Arial, sans-serif; color: ${c.navyDeep}; font-size: 10pt; padding-bottom: 12mm;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.ltr { direction: ltr; unicode-bidi: isolate; }
header.brand { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 10px; border-bottom: 3px solid ${c.red}; }
header.brand img { height: 100px; }
header.brand .title { text-align: left; }
header.brand h1 { margin: 0; font-size: 18pt; font-weight: 800; color: ${c.navy}; }
header.brand p { margin: 4px 0 0; font-size: 9.5pt; color: ${c.muted}; }
.job { margin: 14px 0; padding: 10px 12px; background: ${c.navy}; color: #fff; border-radius: 6px; }
.job .name { font-size: 14pt; font-weight: 800; }
.job .meta { margin-top: 3px; font-size: 10pt; color: ${c.steel}; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
th { background: ${c.navy}; color: #fff; font-weight: 600; text-align: right; padding: 7px 9px; font-size: 9.5pt; }
td { padding: 7px 9px; border-bottom: 1px solid #e3e7ea; vertical-align: top; }
tbody tr:nth-child(even) td { background: #f5f7f8; }
tr { page-break-inside: avoid; }
.when { width: 108px; white-space: nowrap; color: ${c.muted}; text-align: right; }
.actor { font-weight: 700; }
.kind { font-size: 8pt; color: ${c.muted}; }
.kind-system { color: ${c.red}; }
.action { font-weight: 600; }
.detail { font-size: 9pt; color: #46525e; margin-top: 2px; }
.empty { text-align: center; color: ${c.muted}; padding: 18px; }
.count { margin-top: 8px; font-size: 9pt; color: ${c.muted}; }
footer.brand { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; display: flex; justify-content: space-between;
  font-size: 8.5pt; color: ${c.muted}; border-top: 1px solid #d5dade; padding-top: 5px; }
</style>
</head>
<body>
<header class="brand">
  <img src="${e(input.logoSrc)}" alt="${e(BRAND.name)}" />
  <div class="title">
    <h1>יומן פעילות</h1>
    <p>הופק: <span class="ltr">${generated}</span></p>
  </div>
</header>
<div class="job">
  <div class="name">${e(input.job.name)}</div>
  <div class="meta">${e(formatReportJobDate(input.job.date))} · ${e(input.job.reportTime)} · ${e(input.job.location)}</div>
</div>
<table>
  <thead><tr><th>מתי</th><th>מי</th><th>מה</th></tr></thead>
  <tbody>${body}
  </tbody>
</table>
<p class="count">${rows.length} פעולות</p>
<footer class="brand"><span>${e(BRAND.pdf.footer)}</span><span>הופק: <span class="ltr">${generated}</span></span></footer>
</body>
</html>`;
}
